import React, { useState } from "react";
import { ShieldCheck, ShieldAlert, ShieldX, Search, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const RISK_CONFIG = {
  low: { icon: ShieldCheck, color: "text-green-400", border: "border-green-400/30", bg: "bg-green-400/10", label: "Low Risk" },
  medium: { icon: ShieldAlert, color: "text-yellow-400", border: "border-yellow-400/30", bg: "bg-yellow-400/10", label: "Medium Risk" },
  high: { icon: ShieldX, color: "text-red-400", border: "border-red-400/30", bg: "bg-red-400/10", label: "High Risk" },
};

const CHAINS = [
  { id: "1", label: "Ethereum" },
  { id: "56", label: "BNB Smart Chain" },
  { id: "137", label: "Polygon" },
  { id: "42161", label: "Arbitrum" },
  { id: "8453", label: "Base" },
  { id: "10", label: "Optimism" },
];

export default function WalletScanner() {
  const [address, setAddress] = useState("");
  const [chainId, setChainId] = useState("1");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const scan = async (e) => {
    e.preventDefault();
    const trimmed = address.trim();
    if (!trimmed.startsWith("0x") || trimmed.length !== 42) {
      toast.error("Enter a valid wallet address (0x... 42 characters)");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/tools/wallet-scan", { params: { address: trimmed, chain_id: chainId } });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not scan this address");
    } finally {
      setLoading(false);
    }
  };

  const config = result ? RISK_CONFIG[result.risk_level] : null;
  const Icon = config?.icon;

  return (
    <div className="pt-28 pb-20 max-w-2xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-10">
        <h1 className="font-display font-bold text-4xl text-[var(--text-primary)]">Wallet Risk Scanner</h1>
        <p className="mt-3 text-[var(--text-secondary)]">
  Check any EVM wallet address, Ethereum, BNB Smart Chain, Polygon, Arbitrum, Base, or Optimism, for known scam, phishing, or malicious activity flags before sending funds or interacting with it.
</p>
      </div>

      <form onSubmit={scan} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
  <label className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
    Network
  </label>
  <select
    value={chainId}
    onChange={(e) => setChainId(e.target.value)}
    className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400"
  >
    {CHAINS.map((c) => (
      <option key={c.id} value={c.id}>{c.label}</option>
    ))}
  </select>

  <label className="mt-4 block text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
    Wallet Address
  </label>
  <div className="mt-2 flex flex-col sm:flex-row gap-2">
  <input
    type="text"
    value={address}
    onChange={(e) => setAddress(e.target.value)}
    placeholder="0x..."
    className="flex-1 w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm font-mono text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-yellow-400"
  />
  <button
    type="submit"
    disabled={loading}
    className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-3 hover:bg-yellow-500 transition disabled:opacity-50 flex items-center justify-center gap-2 w-full sm:w-auto"
  >
    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
    Scan
  </button>
</div>
      </form>

      {result && (
        <div className={"mt-6 border p-6 " + config.border + " " + config.bg}>
          <div className="flex items-center gap-3">
            <Icon className={"w-8 h-8 " + config.color} />
            <div>
              <div className={"font-display font-bold text-xl " + config.color}>{config.label}</div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] mt-0.5">{result.address}</div>
            </div>
          </div>

          {result.risk_flags.length > 0 ? (
            <ul className="mt-5 space-y-2">
              {result.risk_flags.map((flag, i) => (
                <li key={i} className="text-sm text-[var(--text-secondary)] flex items-start gap-2">
                  <span className={config.color}>•</span> {flag}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-5 text-sm text-[var(--text-secondary)]">
              No known risk flags found for this address based on available security data. This does not guarantee the address is completely safe, always verify independently before sending significant funds.
            </p>
          )}
        </div>
      )}

      <p className="mt-6 text-xs text-[var(--text-tertiary)] text-center">
  Supports Ethereum, BNB Smart Chain, Polygon, Arbitrum, Base, and Optimism addresses. Risk data provided by GoPlus Security. This tool checks for known phishing, scam, and malicious activity flags but cannot guarantee complete safety. Always do your own research.
</p>

<div className="mt-16 border-t border-[var(--border-soft)] pt-10">
        <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">How the Wallet Scanner works</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <ShieldCheck className="w-5 h-5 text-green-400 mb-3" />
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">Low Risk</div>
            <p className="text-xs text-[var(--text-secondary)]">No known scam, phishing, or malicious activity flags found for this address.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <ShieldAlert className="w-5 h-5 text-yellow-400 mb-3" />
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">Medium Risk</div>
            <p className="text-xs text-[var(--text-secondary)]">Some flags present. Review the details below before interacting with this address.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <ShieldX className="w-5 h-5 text-red-400 mb-3" />
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">High Risk</div>
            <p className="text-xs text-[var(--text-secondary)]">Multiple serious flags found. Sending funds or approving this address is not recommended.</p>
          </div>
        </div>

        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
          The scanner checks a wallet address against GoPlus Security's threat intelligence database, which tracks reports of phishing, rug pulls, drainer contracts, and other malicious on-chain activity across six networks. Paste any address before sending funds, approving a token spend, or connecting your wallet to a new site, and the scanner returns a risk level along with the specific flags found.
        </p>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          A low risk result means no known flags exist today, not that an address is guaranteed safe. New scam wallets appear constantly and take time to get reported. Always verify contracts independently, especially before large transactions.
        </p>
      </div>
    </div>
  );
}